import { useState } from "react";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";

import { isValidationError } from "../utils/is-validation-error.ts";

interface ServerError {
  errors?: Record<string, string>;
  message: string;
}

interface UseServerErrorsOptions<FormType extends FieldValues> {
  fieldNameMapping?: Record<string, Path<FormType>>;
  formMethods: UseFormReturn<FormType>;
}

interface UseServerErrorsReturn {
  clearServerErrors: () => void;
  handleServerError: (error: unknown) => void;
  serverErrors: Record<string, string>;
}

export const useServerErrors = <FormType extends FieldValues>({
  fieldNameMapping = {},
  formMethods,
}: UseServerErrorsOptions<FormType>): UseServerErrorsReturn => {
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const clearServerErrors = (): void => {
    setServerErrors({});
  };

  const handleServerError = (error: unknown): void => {
    const mappedErrors: Record<string, string> = {};
    const unmappedErrors: Record<string, string> = {};

    setServerErrors({});

    let serverErrorData: null | ServerError = null;

    if (
      error &&
      typeof error === "object" &&
      "response" in error &&
      error.response &&
      typeof error.response === "object" &&
      "data" in error.response
    ) {
      serverErrorData = error.response.data as ServerError;
    } else if (error && typeof error === "object" && "data" in error) {
      serverErrorData = (error as { data: ServerError }).data;
    } else if (isValidationError(error)) {
      serverErrorData = { errors: error.errors, message: "Validation error" };
    }

    if (serverErrorData?.errors) {
      // Process server errors
      Object.entries(serverErrorData.errors).forEach(
        ([serverField, errorMessage]) => {
          const formField = fieldNameMapping[serverField];

          if (formField) {
            // Map to form field
            formMethods.setError(formField, {
              message: errorMessage,
              type: "server",
            });
            mappedErrors[formField as string] = errorMessage;
          } else {
            // Keep unmapped errors for general display
            unmappedErrors[serverField] = errorMessage;
          }
        },
      );
    } else {
      // Generic error without specific field mapping
      unmappedErrors["general"] =
        serverErrorData?.message || "An unexpected error occurred";
    }

    // Set unmapped errors for display
    if (Object.keys(unmappedErrors).length > 0) {
      setServerErrors(unmappedErrors);
    }
  };

  return {
    clearServerErrors,
    handleServerError,
    serverErrors,
  };
};
